/**
 * Processes an array of items concurrently with a maximum concurrency limit
 * Ensures no more than maxConcurrent promises are executing at once
 *
 * @param items - Array of items to process
 * @param maxConcurrent - Maximum number of concurrent operations
 * @param fn - Async function to execute for each item
 * @param signal - Optional AbortSignal to cancel processing of new items
 */
export async function processConcurrently<T>(
  items: T[],
  maxConcurrent: number,
  fn: (item: T) => Promise<void>,
  signal?: AbortSignal
): Promise<void> {
  const executing = new Set<Promise<void>>();

  for (const item of items) {
    if (signal?.aborted) break;

    // Wait for any executing promise to complete before starting the next
    while (executing.size >= maxConcurrent) {
      await Promise.race(executing);
      if (signal?.aborted) break;
    }

    if (signal?.aborted) break;

    const promise = fn(item).finally(() => {
      executing.delete(promise);
    });

    executing.add(promise);
  }

  await Promise.all(executing);
}
